"use client"

import { useState, type ReactNode } from "react"
import { CalendarIcon, Check, Loader2, Plus, X } from "lucide-react"
import { format } from "date-fns"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  FieldShell,
  FIELD_LABEL,
  FIELD_MONO,
  FIELD_UNDERLINE,
  Segmented,
} from "@/components/graphite/field"
import { PlacementContextSelector } from "@/components/placement-context-selector"
import { useIsMobile } from "@/hooks/use-mobile"
import {
  usePlacementBooking,
  type PlacementBookingArgs,
} from "@/hooks/use-placement-booking"
import { formatMoney, formatPercent } from "@/lib/placement-form"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { PlacementRequestFeeType as FeeType } from "openapi/api/model/placementRequestFeeType"
import type { PlacementRequestPlacementType as PlacementType } from "openapi/api/model/placementRequestPlacementType"
import type { PlacementSplitInputOwnerType as OwnerType } from "openapi/api/model/placementSplitInputOwnerType"

/**
 * Booking a placement, from canvas artboards 15a (wide) and 15b (narrow), with
 * 15e/15f as the light pair.
 *
 * "The drop already said who and where, so the dialog states it and never asks
 * again — no context selector, no job picker. Fee is the one number the old
 * form made you work out yourself: enter salary and a percentage and the value
 * appears in mono beside it. Splits show money, not just percentage, and the
 * allocation total sits where you would look for an error."
 *
 * The placements page opens the same dialog with no context at all, which is
 * the one case that still needs the selector.
 */

const PLACEMENT_TYPES = ["Full Time", "Part Time", "Contract"] as const
const CURRENCIES = ["$", "€", "£", "¥"] as const
const OWNER_TYPES: readonly OwnerType[] = ["recruiter", "referrer", "agency", "external"]

interface PlacementDialogProps {
  isOpen: boolean
  onClose: () => void
  jobTitle?: string
  companyName?: string
  candidateId?: string
  jobCandidateId?: string
  candidateName?: string
  jobId?: string
  creationDate?: string
  /** The stage the drop landed on, named in the header. */
  stage?: string
}

export function PlacementDialog({ stage = "hired", ...args }: PlacementDialogProps) {
  const booking = usePlacementBooking(args as PlacementBookingArgs)
  const isMobile = useIsMobile()

  const body = (
    <PlacementForm
      booking={booking}
      stage={stage}
      compact={isMobile}
      onCancel={args.onClose}
    />
  )

  if (isMobile) {
    return (
      <Sheet open={args.isOpen} onOpenChange={(open) => !open && args.onClose()}>
        <SheetContent
          side="bottom"
          hideClose
          className="flex h-[92vh] flex-col gap-0 rounded-t-2xl p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex flex-none items-center gap-2 border-b px-2 pb-3">
            <button
              type="button"
              onClick={args.onClose}
              className="inline-flex h-11 items-center px-3 text-[13px] text-muted-foreground"
            >
              Cancel
            </button>
            <SheetTitle className="min-w-0 flex-1 truncate text-center text-[14.5px] font-semibold">
              Placement
            </SheetTitle>
            <button
              type="button"
              onClick={booking.submit}
              disabled={!booking.canSave || booking.isSaving}
              className="inline-flex h-11 items-center px-3 text-[13px] font-medium text-brand-ink disabled:opacity-40"
            >
              {booking.isSaving ? "Booking…" : "Book"}
            </button>
          </div>
          {body}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={args.isOpen} onOpenChange={(open) => !open && args.onClose()}>
      <DialogContent
        /* Nothing opens pre-focused: a lit underline reads as a field already
           answered, and the first one here holds a default that usually
           stands. */
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-[620px] flex-col gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">Book the placement</DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  )
}

type Booking = ReturnType<typeof usePlacementBooking>

function PlacementForm({
  booking,
  stage,
  compact,
  onCancel,
}: {
  booking: Booking
  stage: string
  compact: boolean
  onCancel: () => void
}) {
  const { draft, register, setValue } = booking
  const [startOpen, setStartOpen] = useState(false)
  const [endOpen, setEndOpen] = useState(false)

  const isComputedFee = draft.feeType === "percentage"
  const pad = compact ? "px-[18px]" : "px-[26px]"

  return (
    <form onSubmit={booking.submit} className="flex min-h-0 flex-1 flex-col">
      <header className={cn("flex-none border-b pb-[14px]", pad, compact ? "pt-3.5" : "pt-5")}>
        {!compact && (
          <div className="text-[16px] font-semibold leading-[1.2] tracking-[-0.02em]">
            Book the placement
          </div>
        )}
        <p
          className={cn(
            "text-[12.5px] leading-[1.5] text-muted-foreground",
            !compact && "mt-2",
          )}
        >
          {booking.candidateName || "This candidate"} moved to{" "}
          <span style={{ color: stageHex(stage) }}>{stage}</span>
          {booking.jobTitle ? ` · ${booking.jobTitle}` : ""}
          {booking.companyName && !compact ? ` @ ${booking.companyName}` : ""}
        </p>
        {(booking.daysInPipeline !== undefined || booking.openedAt) && (
          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
            {booking.daysInPipeline !== undefined && (
              <span>
                {booking.daysInPipeline} day{booking.daysInPipeline === 1 ? "" : "s"} in
                pipeline
              </span>
            )}
            {booking.openedAt && !compact && (
              <span>opened {format(new Date(booking.openedAt), "d MMM yyyy")}</span>
            )}
          </div>
        )}
      </header>

      {/* On a phone the action bar sits under the scroll area, so the last
          field needs room to clear it rather than ending flush against it. */}
      <div className={cn("min-h-0 flex-1 overflow-y-auto pt-5", pad, compact ? "pb-10" : "pb-6")}>
        {!booking.hasFixedContext && (
          <div className="mb-6">
            <PlacementContextSelector {...booking.contextSelector} />
          </div>
        )}

        {booking.isResolving && (
          <Notice busy>Finding the pipeline record this placement writes to.</Notice>
        )}

        {booking.hasResolutionError && (
          <Notice tone="bad">
            {booking.resolutionErrored
              ? "The job–candidate record for this placement could not be loaded."
              : "There is no job–candidate record for this placement."}
          </Notice>
        )}

        <Segmented
          label="Placement type"
          options={PLACEMENT_TYPES}
          value={draft.placementType}
          onChange={(value) => setValue("placementType", value as PlacementType)}
        />

        <div className={cn("mt-5 flex gap-5", compact && "gap-[18px]")}>
          <FieldShell label="Salary" className="flex-[1.2]">
            <div className="flex items-baseline gap-1.5 border-b pb-[7px]">
              {/* 15a writes a bare "$", but the currency was always pickable
                  and the design is not a reason to take it away. */}
              <select
                {...register("salaryCurrency")}
                aria-label="Salary currency"
                className="bg-transparent font-mono text-[13px] text-muted-foreground outline-none"
              >
                {CURRENCIES.map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </select>
              <input
                {...register("salary")}
                inputMode="decimal"
                placeholder="0"
                aria-label="Salary"
                className={cn(FIELD_UNDERLINE, FIELD_MONO, "border-b-0 pb-0")}
              />
            </div>
          </FieldShell>

          <FieldShell label="Fee" className="w-[104px] flex-none">
            <div className="flex items-baseline gap-1.5 border-b pb-[7px]">
              <input
                {...register("fee")}
                inputMode="decimal"
                placeholder="0"
                aria-label="Fee"
                className={cn(FIELD_UNDERLINE, FIELD_MONO, "border-b-0 pb-0")}
              />
              <button
                type="button"
                onClick={() =>
                  setValue("feeType", (isComputedFee ? "fixed" : "percentage") as FeeType)
                }
                aria-label={`Fee is ${isComputedFee ? "a percentage" : "a fixed amount"}; switch`}
                className="font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                {isComputedFee ? "%" : "$"}
              </button>
            </div>
          </FieldShell>

          {!compact && (
            <FieldShell label="Fee amount" className="w-[118px] flex-none">
              <div className="border-b pb-[7px] font-mono text-[13px] text-muted-foreground">
                {formatMoney(booking.fee, draft.salaryCurrency)}
              </div>
            </FieldShell>
          )}
        </div>

        {compact && (
          <FieldShell label="Fee amount" className="mt-4">
            <div className="border-b pb-2 font-mono text-[15px] text-foreground">
              {formatMoney(booking.fee, draft.salaryCurrency)}
            </div>
          </FieldShell>
        )}

        {isComputedFee && (
          <p className="mt-2.5 text-[11px] leading-[1.5] text-muted-foreground">
            Fee amount is computed. Switch to a fixed fee and it becomes editable.
          </p>
        )}

        <div className={cn("mt-5 flex gap-5", compact && "gap-[18px]")}>
          <DateField
            label="Start date"
            value={draft.startDate}
            open={startOpen}
            onOpenChange={setStartOpen}
            onSelect={(date) => setValue("startDate", date)}
            disabled={draft.endDate ? { after: draft.endDate } : undefined}
          />
          <DateField
            label="End date"
            value={draft.endDate}
            open={endOpen}
            onOpenChange={setEndOpen}
            onSelect={(date) => setValue("endDate", date)}
            disabled={draft.startDate ? { before: draft.startDate } : undefined}
          />
          {!compact && <CreatedField value={draft.createdDate} />}
        </div>

        {compact && <CreatedField value={draft.createdDate} className="mt-4" />}

        {compact ? (
          <>
            <Splits booking={booking} compact />
            <Notes register={register} className="mt-5" />
          </>
        ) : (
          <>
            <Notes register={register} className="mt-5" />
            <Splits booking={booking} />
          </>
        )}
      </div>

      {compact ? (
        <div className="flex-none border-t px-[18px] py-3">
          <button
            type="submit"
            disabled={!booking.canSave || booking.isSaving}
            className="flex h-11 w-full items-center justify-between rounded-lg bg-primary/[0.08] px-4 text-[13px] font-medium text-brand-ink disabled:opacity-40"
          >
            <span className="inline-flex items-center gap-2">
              {booking.isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Book placement
            </span>
            <span className="font-mono text-[11.5px] text-muted-foreground">
              {formatMoney(booking.fee, draft.salaryCurrency)}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex flex-none items-center gap-[22px] border-t px-[26px] pb-5 pt-[18px]">
          <button
            type="submit"
            disabled={!booking.canSave || booking.isSaving}
            className="inline-flex items-center gap-2 text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {booking.isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Book placement
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">
            {formatMoney(booking.fee, draft.salaryCurrency)}
          </span>
        </div>
      )}
    </form>
  )
}

/** Read-only, and stated rather than hidden — the old form showed it too. */
function CreatedField({ value, className }: { value: Date; className?: string }) {
  return (
    <FieldShell label="Created" className={cn("flex-1", className)}>
      <div className="border-b pb-[7px] font-mono text-[13px] text-muted-foreground">
        {format(value, "d MMM yyyy")}
      </div>
    </FieldShell>
  )
}

function Notes({
  register,
  className,
}: {
  register: Booking["register"]
  className?: string
}) {
  return (
    <FieldShell label="Notes" className={className}>
      <textarea
        {...register("notes")}
        rows={3}
        placeholder="Anything worth remembering about this placement…"
        aria-label="Notes"
        className={cn(
          FIELD_UNDERLINE,
          "resize-none pb-2.5 text-[12.5px] leading-[1.7]",
        )}
      />
    </FieldShell>
  )
}

/**
 * "Splits show money, not just percentage, and the allocation total sits where
 * you would look for an error."
 */
function Splits({ booking, compact }: { booking: Booking; compact?: boolean }) {
  const { splitFields, splitValues, register, draft } = booking

  return (
    <section className="mt-[22px]">
      <div className="flex items-baseline gap-3.5">
        <div className={FIELD_LABEL}>Splits</div>
        <span
          className={cn(
            "ml-auto font-mono text-[11px]",
            // Green means "this already happened"; the token darkens on white.
            booking.allocationBalanced ? "text-success" : "text-destructive",
          )}
        >
          {formatPercent(booking.allocated)}% allocated
        </span>
        <button
          type="button"
          onClick={() =>
            splitFields.append({ owner: "", ownerType: "recruiter", percentage: "" })
          }
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand-ink transition-opacity hover:opacity-80"
        >
          <Plus className="h-3 w-3" />
          Add
        </button>
      </div>

      <div className="mt-3">
        {!compact && (
          <div className="flex items-baseline gap-3 pb-[9px] text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
            <span className="flex-1">Owner</span>
            <span className="w-[86px] flex-none">Type</span>
            <span className="w-[42px] flex-none text-right">%</span>
            <span className="w-[78px] flex-none text-right">Value</span>
            <span className="w-3.5 flex-none" />
          </div>
        )}

        {splitFields.fields.map((field, index) => (
          <div
            key={field.id}
            className={cn(
              "border-t py-[11px]",
              compact
                ? "grid grid-cols-[1fr_auto_1.25rem] items-center gap-x-3 gap-y-1"
                : "flex items-baseline gap-3",
            )}
          >
            <input
              {...register(`splits.${index}.owner`)}
              placeholder="Owner name or email"
              aria-label={`Split ${index + 1} owner`}
              className={cn(
                FIELD_UNDERLINE,
                "border-b-0 pb-0 text-[12.5px] leading-[1.4]",
                !compact && "flex-1",
              )}
            />

            <select
              {...register(`splits.${index}.ownerType`)}
              aria-label={`Split ${index + 1} owner type`}
              className={cn(
                FIELD_UNDERLINE,
                "border-b-0 pb-0 font-mono text-[11px] text-muted-foreground",
                compact ? "col-start-1 row-start-2" : "w-[86px] flex-none",
              )}
            >
              {OWNER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <div
              className={cn(
                "flex items-baseline justify-end gap-3",
                compact ? "col-start-2 row-span-2 row-start-1" : "contents",
              )}
            >
              <input
                {...register(`splits.${index}.percentage`)}
                inputMode="decimal"
                placeholder="0"
                aria-label={`Split ${index + 1} percentage`}
                className={cn(
                  FIELD_UNDERLINE,
                  "border-b-0 pb-0 text-right font-mono text-[12px]",
                  compact ? "w-12" : "w-[42px] flex-none",
                )}
              />
              <span
                className={cn(
                  "text-right font-mono text-[12px] text-muted-foreground",
                  compact ? "w-[78px]" : "w-[78px] flex-none",
                )}
              >
                {formatMoney(splitValues[index], draft.salaryCurrency)}
              </span>
            </div>

            <div
              className={cn(
                "flex w-3.5 flex-none justify-end",
                compact && "col-start-3 row-span-2 row-start-1",
              )}
            >
              {splitFields.fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => splitFields.remove(index)}
                  aria-label={`Remove split ${index + 1}`}
                  className="text-muted-foreground transition-colors hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {!booking.allocationBalanced && draft.splits?.length ? (
        <p className="mt-2 text-[11px] text-destructive">
          Splits must total 100% before this can be booked.
        </p>
      ) : null}
    </section>
  )
}

/** A flat notice. Not a card: the dialog is already the surface. */
function Notice({
  children,
  tone = "neutral",
  busy,
}: {
  children: ReactNode
  tone?: "neutral" | "bad"
  busy?: boolean
}) {
  return (
    <div
      className={cn(
        "mb-5 flex items-center gap-2 border-l-2 py-1 pl-3 text-[12px] text-muted-foreground",
        tone === "bad" ? "border-destructive" : "border-border",
      )}
    >
      {busy ? <Loader2 className="h-3 w-3 flex-none animate-spin" /> : null}
      {children}
    </div>
  )
}

function DateField({
  label,
  value,
  open,
  onOpenChange,
  onSelect,
  disabled,
}: {
  label: string
  value: Date | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (date: Date | undefined) => void
  disabled?: { before: Date } | { after: Date }
}) {
  return (
    <FieldShell label={label} className="flex-1">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={value ? `${label}, ${format(value, "EEEE d MMMM yyyy")}` : label}
            className={cn(
              FIELD_UNDERLINE,
              FIELD_MONO,
              "flex items-center justify-between pb-[7px] text-left",
              !value && "text-muted-foreground",
            )}
          >
            {value ? format(value, "d MMM yyyy") : "—"}
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              onSelect(date)
              onOpenChange(false)
            }}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </FieldShell>
  )
}
